import { TEMPLATE_WIDGET_IDENTIFIER_PREFIX } from '@/components/template-builder/utils/constants';
// eslint-disable-next-line import/no-cycle
import { generateUniqStructureId } from '@/components/template-builder/utils/helpers';
import {
  substituteIntegrationWidgetUrl,
  extractIntegrationPreviewValues,
} from '@/components/template-builder/utils/integration-widget-url';

/**
 * Generic declarative integration-widget producer — SHARED across editor targets
 * (email = `<table>`, popup / smart_content = `<div>`). Imported by WidgetsPanel
 * for every target, so it lives here rather than under a per-type `templates/`
 * folder (unlike native widgets, which duplicate their producers per type).
 *
 * Produces a self-contained block differentiated by `data-render-type`:
 *  - `image`: an `<img>` whose URL is rebuilt from `data-url-template` (with
 *    `{integration.*}` resolved at drop) + `data-config` (`{widget.*}` values),
 *    no server call.
 *  - `html` (static or dynamic): a `.spm_integration_widget_content` cell
 *    filled by `POST /v1/widgets/render` (server-side rendered + sanitized). A
 *    loading placeholder shows until the first render.
 *
 * A shared `.spm_integration_widget_cell` class on the cell (a `<th>` in email,
 * a `<div>` on web) lets the config style selectors + the render/image setters
 * work on both structures unchanged. `data-config`/`data-schema` stay the source
 * of truth for re-edit. Called at drop by WidgetsPanel.
 */
export const rawWidgetIntegration = (params: {
  slug: string;
  key: string;
  name?: string;
  urlTemplate: string;
  config?: Record<string, unknown>;
  defaultWidth?: number;
  schema?: unknown;
  renderType?: 'image' | 'html';
  renderMode?: 'static' | 'dynamic';
  target?: string;
  integrationName?: string;
  /** D4 — version de la déclaration FIGÉE au drop (snapshot ; défaut 1). */
  version?: number;
}): string => {
  const widgetId = `${TEMPLATE_WIDGET_IDENTIFIER_PREFIX}${generateUniqStructureId()}`;
  const config = params.config || {};
  const width = params.defaultWidth || 600;
  const renderType = params.renderType === 'html' ? 'html' : 'image';
  // render_mode applies to html only; image has none.
  const renderMode = renderType === 'html'
    ? (params.renderMode === 'dynamic' ? 'dynamic' : 'static')
    : undefined;
  const renderModeAttr = renderType === 'html'
    ? `\n    data-render-mode="${renderMode}"`
    : '';
  const target = params.target || 'email_template';
  const alt = (params.name || 'Widget').replace(/"/g, '&quot;');
  const integrationNameAttr = (params.integrationName || '').replace(/"/g, '&quot;');
  // data-config / data-schema sit in single-quoted attributes → escape single quotes.
  const configAttr = JSON.stringify(config).replace(/'/g, '&#39;');
  const schemaAttr = JSON.stringify(params.schema ?? {}).replace(/'/g, '&#39;');
  const urlTemplateAttr = (params.urlTemplate || '').replace(/"/g, '&quot;');

  // Web targets (popup / smart_content) use <div>-based blocks like the rest of
  // their widgets; email uses <table>. The shared `spm_integration_widget_cell`
  // class on the cell (a <th> in email, a <div> on web) lets the config style
  // selectors + the render/image setters work on both structures unchanged.
  const isWeb = target === 'popup' || target === 'smart_content';
  const wrapperClass = isWeb
    ? 'spm_widget_integration spm_draggable_widget spm_widget'
    : 'spm_widget_integration spm_draggable_widget';

  const commonAttrs = `id="${widgetId}"
    class="${wrapperClass}"
    data-widgettype="spm_widget_integration"
    data-integration-slug="${params.slug}"
    data-widget-key="${params.key}"
    data-widget-name="${alt}"
    data-integration-name="${integrationNameAttr}"
    data-render-type="${renderType}"${renderModeAttr}
    data-target="${target}"
    data-widget-version="${Number(params.version) > 0 ? Number(params.version) : 1}"
    data-config='${configAttr}'
    data-schema='${schemaAttr}'`;

  const loadingCell = '<div class="spm_integration_widget_loading" style="padding: 24px; color: #9ca3af; font-family: Arial, sans-serif; font-size: 13px;">…</div>';

  if (renderType === 'html') {
    // html (static or dynamic) — a rendered cell filled by POST /widgets/render.
    if (isWeb) {
      return `<div ${commonAttrs}>
    <div class="spm_integration_widget_cell spm_integration_widget_content" style="text-align: center;">
      ${loadingCell}
    </div>
  </div>`;
    }
    return `<table cellspacing="0" cellpadding="0" width="100%"
    ${commonAttrs}
    style="border-collapse: collapse;">
    <tbody>
      <tr>
        <th class="spm_integration_widget_cell spm_integration_widget_content" style="text-align: center;">
          ${loadingCell}
        </th>
      </tr>
    </tbody>
  </table>`;
  }

  const initialSrc = substituteIntegrationWidgetUrl(
    params.urlTemplate,
    config,
    extractIntegrationPreviewValues(params.schema),
  );
  const imgTag = `<img
        class="spm_integration_widget_img spm_no_translate"
        src="${initialSrc}"
        alt="${alt}"
        width="${width}"
        style="max-width: 100%; height: auto; display: inline-block;"
      />`;
  if (isWeb) {
    return `<div ${commonAttrs}
    data-url-template="${urlTemplateAttr}">
    <div class="spm_integration_widget_cell" style="text-align: center;">
      ${imgTag}
    </div>
  </div>`;
  }
  return `<table cellspacing="0" cellpadding="0" width="100%"
    ${commonAttrs}
    data-url-template="${urlTemplateAttr}"
    style="border-collapse: collapse;">
    <tbody>
      <tr>
        <th class="spm_integration_widget_cell" style="text-align: center;">
          ${imgTag}
        </th>
      </tr>
    </tbody>
  </table>`;
};
